import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, Package, LogOut, Check, Truck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["pending_payment", "paid", "shipped", "delivered"];
const LABEL = {
  pending_payment: "Pending payment", paid: "Paid", shipped: "Shipped", delivered: "Delivered",
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("fs_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("orders");
  const [products, setProducts] = useState([]);
  const [soldOut, setSoldOut] = useState([]);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadOrders = async (t = token) => {
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${t}` } });
      setOrders(res.data.orders);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  };

  const loadStock = async (t = token) => {
    try {
      const [p, s] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/stock`, { headers: { Authorization: `Bearer ${t}` } }),
      ]);
      setProducts(p.data.products);
      setSoldOut(s.data.sold_out || []);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  };

  useEffect(() => {
    if (token) { loadOrders(); loadStock(); }
    // eslint-disable-next-line
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("fs_admin_token", res.data.token);
      setToken(res.data.token);
      await Promise.all([loadOrders(res.data.token), loadStock(res.data.token)]);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("fs_admin_token");
    setToken(""); setOrders([]);
  };

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/admin/orders/${id}`, { status }, auth);
    loadOrders();
  };

  const toggleStock = async (key) => {
    const isOut = soldOut.includes(key);
    const res = await axios.post(`${API}/admin/stock`, { key, sold_out: !isOut }, auth);
    setSoldOut(res.data.sold_out || []);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[var(--bg)]">
        <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={login} className="w-full max-w-sm border border-[var(--line)] p-8 bg-[var(--surface)]">
          <div className="flex items-center gap-3 mb-8">
            <Lock className="w-6 h-6 text-[var(--accent)]" />
            <h1 className="font-anton uppercase text-3xl tracking-tight">Admin</h1>
          </div>
          <label className="uppercase tracking-widest text-xs text-[var(--text-dim)] block mb-2">Email</label>
          <input data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[var(--bg)] border border-[var(--line)] px-4 py-3 mb-4 focus:outline-none focus:border-white" />
          <label className="uppercase tracking-widest text-xs text-[var(--text-dim)] block mb-2">Password</label>
          <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-[var(--bg)] border border-[var(--line)] px-4 py-3 mb-6 focus:outline-none focus:border-white" />
          {error && <p className="text-[var(--accent)] text-sm mb-4" data-testid="admin-error">{error}</p>}
          <button data-testid="admin-login-btn" disabled={loading}
            className="w-full bg-[var(--accent)] text-white py-3 uppercase tracking-widest text-sm font-bold hover:bg-white hover:text-black transition-colors disabled:opacity-50">
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="border-b border-[var(--line)] px-6 md:px-12 h-20 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-[var(--accent)]" />
            <h1 className="font-anton uppercase text-2xl tracking-tight">Admin</h1>
          </div>
          <nav className="flex items-center gap-2">
            <button data-testid="tab-orders" onClick={() => setTab("orders")}
              className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${tab === "orders" ? "bg-[var(--text)] text-black border-[var(--text)]" : "border-[var(--line)] text-[var(--text-dim)] hover:border-white hover:text-white"}`}>
              Orders ({orders.length})
            </button>
            <button data-testid="tab-stock" onClick={() => setTab("stock")}
              className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${tab === "stock" ? "bg-[var(--text)] text-black border-[var(--text)]" : "border-[var(--line)] text-[var(--text-dim)] hover:border-white hover:text-white"}`}>
              Stock
            </button>
          </nav>
        </div>
        <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 text-sm uppercase tracking-widest text-[var(--text-dim)] hover:text-white">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </header>

      {tab === "stock" ? (
        <div className="p-6 md:p-12 space-y-6 max-w-[1400px] mx-auto">
          <p className="text-[var(--text-dim)] text-sm uppercase tracking-widest">Tap a colour to sell it out entirely, or tap individual sizes. Sold-out items are hidden from buyers.</p>
          {products.map((p) => (
            <div key={p.id} data-testid={`stock-${p.id}`} className="border border-[var(--line)] p-6 bg-[var(--surface)]">
              <h3 className="font-anton uppercase text-xl tracking-tight mb-4">{p.name} <span className="text-[var(--accent)] text-sm">{p.price} KSH</span></h3>
              <div className="space-y-4">
                {p.colors.map((c) => {
                  const colorKey = `${p.id}::${c.name}`;
                  const colorOut = soldOut.includes(colorKey);
                  return (
                    <div key={c.name} className="flex items-center gap-4 flex-wrap">
                      <button data-testid={`stock-color-${p.id}-${c.name.toLowerCase()}`} onClick={() => toggleStock(colorKey)}
                        className={`flex items-center gap-2 px-3 py-2 border text-xs uppercase tracking-widest transition-colors ${colorOut ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "border-[var(--line)] hover:border-white"}`}>
                        <span className="w-4 h-4 rounded-full border border-white/30" style={{ backgroundColor: c.hex }} />
                        {c.name} {colorOut ? "— sold out" : ""}
                      </button>
                      <div className="flex gap-2">
                        {["S", "M", "L", "XL", "XXL"].map((s) => {
                          const sizeKey = `${p.id}::${c.name}::${s}`;
                          const sizeOut = colorOut || soldOut.includes(sizeKey);
                          return (
                            <button key={s} data-testid={`stock-size-${p.id}-${c.name.toLowerCase()}-${s}`} onClick={() => !colorOut && toggleStock(sizeKey)} disabled={colorOut}
                              className={`min-w-9 px-2 py-1.5 border text-xs font-bold uppercase transition-colors ${sizeOut ? "opacity-40 line-through border-[var(--line)] text-[var(--text-dim)]" : "border-[var(--line)] hover:border-white"}`}>
                              {s}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="p-6 md:p-12 space-y-4 max-w-[1400px] mx-auto">
        {orders.length === 0 && (
          <p className="text-[var(--text-dim)] text-center py-20 uppercase tracking-widest text-sm">No orders yet</p>
        )}
        {orders.map((o) => (
          <div key={o.id} data-testid={`admin-order-${o.id}`} className="border border-[var(--line)] p-6 bg-[var(--surface)]">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 font-bold ${o.type === "card" ? "bg-white text-black" : "bg-[var(--accent)] text-white"}`}>{o.type === "card" ? "Card" : "M-Pesa"}</span>
                  <span className="font-anton uppercase text-lg tracking-tight">{o.name}</span>
                  <span className="text-[var(--text-dim)] text-sm">{o.phone}</span>
                </div>
                <p className="text-[var(--text-dim)] text-sm mb-1">{o.location}</p>
                <div className="text-sm mt-2 space-y-0.5">
                  {(o.items || []).map((it, idx) => (
                    <p key={idx}>{it.product_name || "Full Send Tee"} — {it.color}/{it.size} × {it.quantity}</p>
                  ))}
                </div>
                {o.note && <p className="text-[var(--text-dim)] text-xs mt-2 italic">{o.note}</p>}
              </div>
              <div className="text-right">
                <p className="font-anton text-2xl text-[var(--accent)]">{o.amount} KSH</p>
                <p className="text-[var(--text-dim)] text-xs mb-3">{new Date(o.created_at).toLocaleString()}</p>
                <div className="flex flex-wrap gap-2 justify-end">
                  {STATUSES.map((st) => (
                    <button key={st} data-testid={`status-${o.id}-${st}`} onClick={() => setStatus(o.id, st)}
                      className={`text-[11px] uppercase tracking-widest px-3 py-1.5 border transition-colors flex items-center gap-1 ${o.status === st ? "bg-[var(--text)] text-black border-[var(--text)]" : "border-[var(--line)] text-[var(--text-dim)] hover:border-white hover:text-white"}`}>
                      {st === "shipped" && <Truck className="w-3 h-3" />}
                      {o.status === st && st !== "shipped" && <Check className="w-3 h-3" />}
                      {LABEL[st]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
};

export default Admin;
